// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pure helpers for "Waiting on a reply" (design/waiting-replies): which items are up now,
// how they group, how old they are, and where each one lives in the app.

import {
	isWaitingDue,
	type WaitingDirection,
	type WaitingItem,
	type WaitingSource,
} from '@konvo/schema/src/domains/konvo/Waiting';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Items that are up now (open, not put off, past their time), oldest ask first. */
export function dueItems(
	items: ReadonlyArray<WaitingItem>,
	now: number,
	direction?: WaitingDirection,
): Array<WaitingItem> {
	return items
		.filter((item) => (!direction || item.direction === direction) && isWaitingDue(item, now))
		.sort((a, b) => Date.parse(a.asked_at) - Date.parse(b.asked_at));
}

/** Open, not put off, but not up yet (Konvo is still giving it time, or a reminder sets the time). */
export function upcomingItems(items: ReadonlyArray<WaitingItem>, now: number): Array<WaitingItem> {
	return items
		.filter(
			(item) =>
				item.status === 'open' &&
				!(item.snoozed_until != null && Date.parse(item.snoozed_until) > now) &&
				Date.parse(item.due_at) > now,
		)
		.sort((a, b) => Date.parse(a.due_at) - Date.parse(b.due_at));
}

/** Put off until later (Later, Remind me Fri). */
export function snoozedItems(items: ReadonlyArray<WaitingItem>, now: number): Array<WaitingItem> {
	return items.filter(
		(item) => item.status === 'open' && item.snoozed_until != null && Date.parse(item.snoozed_until) > now,
	);
}

/** The item a conversation shows in its bar: up now, the oldest one; waiting on you first. */
export function itemForConversation(
	items: ReadonlyArray<WaitingItem>,
	source: WaitingSource,
	conversationId: string | null | undefined,
	now: number,
	messageId?: string | null,
): WaitingItem | null {
	if (!conversationId && !messageId) return null;
	const here = dueItems(items, now).filter(
		(item) =>
			item.conversation.source === source &&
			((conversationId != null && item.conversation.id === conversationId) ||
				(messageId != null && item.conversation.message_id === messageId)),
	);
	return here.find((item) => item.direction === 'on_you') ?? here[0] ?? null;
}

/** Calendar days since the ask on this device ("Today", "1 day" for yesterday, "4 days"). */
export function ageDays(item: Pick<WaitingItem, 'asked_at'>, now: number): number {
	const asked = new Date(item.asked_at);
	const today = new Date(now);
	const start = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
	return Math.max(0, Math.round((start(today) - start(asked)) / DAY_MS));
}

/** The person's first name, for short lines ("No answer from Arcas yet"). */
export function firstName(name: string): string {
	const clean = name.replace(/^#/, '').trim();
	return clean.split(/\s+/)[0] || clean;
}

/** Initials for an avatar without a picture. */
export function initials(name: string): string {
	const words = name.replace(/^#/, '').trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) return '?';
	const first = words[0]![0] ?? '';
	const last = words.length > 1 ? (words.at(-1)![0] ?? '') : '';
	return `${first}${last}`.toUpperCase();
}

/** "Later" without a time: the next working morning at 09:00 on this device. */
export function nextMorning(now: Date): Date {
	const next = new Date(now);
	next.setHours(9, 0, 0, 0);
	do next.setDate(next.getDate() + 1);
	while (next.getDay() === 0 || next.getDay() === 6);
	return next;
}

/** "Remind me Fri": two working days on, 09:00, or the next Monday when that is the weekend. */
export function remindDay(now: Date): Date {
	const next = new Date(now);
	next.setHours(9, 0, 0, 0);
	let left = 2;
	while (left > 0) {
		next.setDate(next.getDate() + 1);
		if (next.getDay() !== 0 && next.getDay() !== 6) left -= 1;
	}
	return next;
}

export type SnoozeChoice = 'later_today' | 'morning' | 'remind_day' | 'next_week';

/**
 * The times a row's Snooze offers: three hours on (on the half hour, while it is still the working
 * day), the next working morning, the bar's "Remind me" day and next Monday. Duplicates drop out.
 */
export function snoozeChoices(now: Date): Array<{choice: SnoozeChoice; until: Date}> {
	const laterToday = new Date(now.getTime() + 3 * 3600_000);
	const minutes = laterToday.getMinutes();
	laterToday.setMinutes(minutes === 0 ? 0 : minutes <= 30 ? 30 : 60, 0, 0);
	const nextWeek = new Date(now);
	nextWeek.setHours(9, 0, 0, 0);
	nextWeek.setDate(nextWeek.getDate() + ((8 - nextWeek.getDay()) % 7 || 7));
	const choices: Array<{choice: SnoozeChoice; until: Date}> = [
		...(laterToday.getDate() === now.getDate() && laterToday.getHours() <= 18
			? [{choice: 'later_today' as const, until: laterToday}]
			: []),
		{choice: 'morning', until: nextMorning(now)},
		{choice: 'remind_day', until: remindDay(now)},
		{choice: 'next_week', until: nextWeek},
	];
	return choices.filter(
		(entry, index) => choices.findIndex((other) => other.until.getTime() === entry.until.getTime()) === index,
	);
}

/** Where the item lives: the app path of its conversation (the ask's message). */
export function conversationPath(item: WaitingItem): string {
	const {conversation} = item;
	if (conversation.source === 'mail') return `/channels/@me/mail/${encodeURIComponent(conversation.message_id)}`;
	if (conversation.source === 'whatsapp') return `/channels/@me/whatsapp/${encodeURIComponent(conversation.id)}`;
	return conversation.guild_id
		? `/channels/${conversation.guild_id}/${conversation.id}`
		: `/channels/@me/${conversation.id}`;
}
